if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.dialog = this.querySelector('[data-cart-drawer-dialog]');
      this.panel = this.querySelector('.cart-drawer__panel');
      this.returnFocus = null;
      this.quantityTimers = new Map();
      this.reassuranceTimer = null;
      this.reassuranceIndex = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollArea = this.querySelector('[data-cart-drawer-scroll]');

      document.addEventListener('click', event => this.onClick(event), { signal: this.signal });
      document.addEventListener('submit', event => this.onSubmit(event), { capture: true, signal: this.signal });
      document.addEventListener('change', event => this.onChange(event), { signal: this.signal });
      this.dialog?.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal: this.signal });
      this.dialog?.addEventListener('keydown', event => this.onKeydown(event), { signal: this.signal });
      this.addEventListener('scroll', () => this.updateScrollAffordance(), { capture: true, passive: true, signal: this.signal });
      this.observeScrollArea();
      this.selectDrawerView('cart');
      requestAnimationFrame(() => this.updateScrollAffordance());

      if (!window.__themeCartInitialGiftSyncComplete) {
        window.__themeCartInitialGiftSyncComplete = true;
        this.syncThresholdGifts().then(changed => {
          if (changed) this.refresh(false);
        }).catch(() => {});
      }
    }

    get rootRoute() {
      const root = window.Shopify?.routes?.root || '/';
      return root.endsWith('/') ? root : `${root}/`;
    }

    route(path) {
      return `${this.rootRoute}${path}`.replace(/([^:]\/)\/+/g, '$1');
    }

    async request(path, options = {}) {
      const { headers = {}, ...requestOptions } = options;
      const response = await fetch(this.route(path), {
        credentials: 'same-origin',
        ...requestOptions,
        headers: { Accept: 'application/json', ...headers }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || this.dataset.errorMessage || 'Cart update failed.');
      return data;
    }

    async getCart() {
      return this.request('cart.js');
    }

    open(launcher = null) {
      if (!this.dialog) return;
      this.selectDrawerView('cart');
      this.returnFocus = launcher || this.returnFocus || document.activeElement;
      if (!this.dialog.open) {
        try { this.dialog.showModal(); }
        catch { this.dialog.setAttribute('open', ''); }
      }
      document.body.classList.add('cart-drawer-open');
      requestAnimationFrame(() => {
        this.dialog?.classList.add('is-open');
        this.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });
        this.updateScrollAffordance();
      });
      this.startReassurance();
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.classList.remove('is-open');
      this.stopReassurance();
      const finish = () => {
        if (!this.dialog?.open) return;
        this.dialog.close();
        document.body.classList.remove('cart-drawer-open');
        this.selectDrawerView('cart');
        this.returnFocus?.focus?.({ preventScroll: true });
      };
      if (this.reducedMotion) finish();
      else window.setTimeout(finish, 300);
    }

    setBusy(busy, showLoader = true) {
      this.panel?.setAttribute('aria-busy', String(busy));
      if (!this.panel) return;
      this.panel.toggleAttribute('data-loader-visible', Boolean(busy && showLoader));
    }

    setButtonBusy(button, busy) {
      if (!button) return;
      button.toggleAttribute('disabled', busy);
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }

    onClick(event) {
      const opener = event.target.closest('[data-cart-drawer-open], [data-open-cart-drawer]');
      if (opener) {
        event.preventDefault();
        this.open(opener);
        return;
      }

      if (!event.target.closest('cart-drawer')) return;
      if (event.target.closest('[data-cart-drawer-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-cart-scroll-down]')) {
        event.preventDefault();
        this.scrollArea?.scrollBy({
          top: Math.max(120, this.scrollArea.clientHeight * .8),
          behavior: this.reducedMotion ? 'instant' : 'smooth'
        });
        return;
      }

      const tab = event.target.closest('[data-cart-drawer-tab]');
      if (tab) {
        event.preventDefault();
        this.selectDrawerView(tab.dataset.cartDrawerTab, true);
        return;
      }

      const reassuranceDot = event.target.closest('[data-cart-reassurance] [data-slider-dot]');
      if (reassuranceDot) {
        event.preventDefault();
        this.goToReassurance(Number(reassuranceDot.dataset.slideIndex));
        this.startReassurance(false);
        return;
      }

      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.removeLine(remove);
        return;
      }

      const thresholdGiftRemove = event.target.closest('[data-cart-threshold-gift-remove]');
      if (thresholdGiftRemove) {
        event.preventDefault();
        this.dismissThresholdGift(thresholdGiftRemove);
        return;
      }

      const thresholdGiftRestore = event.target.closest('[data-cart-threshold-gift-restore]');
      if (thresholdGiftRestore) {
        event.preventDefault();
        this.restoreThresholdGift(thresholdGiftRestore);
        return;
      }

      const recentAdd = event.target.closest('[data-recently-viewed-add]');
      if (recentAdd) {
        event.preventDefault();
        this.addRecentlyViewed(recentAdd);
        return;
      }

      const giftWrap = event.target.closest('[data-cart-gift-wrap-add]');
      if (giftWrap) {
        event.preventDefault();
        this.addGiftWrap(giftWrap);
        return;
      }

    }

    observeScrollArea() {
      this.scrollObserver?.disconnect();
      this.scrollArea = this.querySelector('[data-cart-drawer-scroll]');
      if (!this.scrollArea) return;
      this.scrollObserver = new ResizeObserver(() => this.updateScrollAffordance());
      this.scrollObserver.observe(this.scrollArea);
      for (const child of this.scrollArea.children) this.scrollObserver.observe(child);
      this.updateScrollAffordance();
    }

    updateScrollAffordance() {
      if (!this.panel || !this.scrollArea) return;
      const hasMore = this.scrollArea.scrollHeight > this.scrollArea.clientHeight + 8
        && this.scrollArea.scrollTop < this.scrollArea.scrollHeight - this.scrollArea.clientHeight - 24;
      this.panel.classList.toggle('has-scroll-below', hasMore);
      const button = this.querySelector('[data-cart-scroll-down]');
      if (button) {
        if (!hasMore && document.activeElement === button) this.scrollArea.focus({ preventScroll: true });
        button.hidden = !hasMore || this.classList.contains('is-recently-viewed');
      }
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest('main-cart')) return;

      if (form.matches('[data-cart-discount-form]')) {
        event.preventDefault();
        event.stopPropagation();
        this.applyDiscount(form);
        return;
      }

      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      if (!form.closest('cart-drawer') && !form.matches('[data-gwe-newcart-submit]')) return;
      if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.addForm(form, event.submitter);
    }

    onChange(event) {
      const input = event.target.closest('cart-drawer .cart-drawer-item .quantity-selector__input');
      if (!input) return;
      const line = input.closest('[data-cart-line-key]');
      if (!line) return;
      const key = line.dataset.cartLineKey;
      window.clearTimeout(this.quantityTimers.get(key));
      this.quantityTimers.set(key, window.setTimeout(() => {
        this.changeLine(key, Math.max(0, Number(input.value) || 0), input);
      }, 220));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      const currentTab = event.target.closest('[data-cart-drawer-tab]');
      if (currentTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        const tabs = Array.from(this.querySelectorAll('[data-cart-drawer-tab]'));
        const currentIndex = tabs.indexOf(currentTab);
        if (currentIndex === -1) return;
        event.preventDefault();
        let nextIndex = currentIndex;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        this.selectDrawerView(tabs[nextIndex].dataset.cartDrawerTab, true);
        return;
      }
      if (event.key === 'Tab') window.ThemeComponents?.focus?.trapTab(event, this.dialog);
    }

    selectDrawerView(viewName, focus = false) {
      const selectedView = viewName === 'recently' ? 'recently' : 'cart';
      const selectedTab = this.querySelector(`[data-cart-drawer-tab="${selectedView}"]`);
      const selectedPanel = this.querySelector(`[data-cart-drawer-view="${selectedView}"]`);
      if (!selectedPanel) return;

      this.querySelectorAll('[data-cart-drawer-tab]').forEach(tab => {
        const active = tab === selectedTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-cart-drawer-view]').forEach(view => {
        view.hidden = view !== selectedPanel;
      });
      this.classList.toggle('is-recently-viewed', selectedView === 'recently');
      requestAnimationFrame(() => this.updateScrollAffordance());

      if (selectedView === 'recently') this.querySelector('recently-viewed-products')?.refresh?.();
      if (focus && !this.reducedMotion) {
        selectedPanel.animate([
          { opacity: .25, transform: 'translate3d(0, 6px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ], { duration: 220, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      }
      if (focus) selectedTab?.focus({ preventScroll: true });
    }

    async applyDiscount(form) {
      const input = form.querySelector('[data-cart-discount-input]');
      const status = form.parentElement?.querySelector('[data-cart-discount-status]');
      const submitter = form.querySelector('button[type="submit"]');
      const code = input?.value?.trim() || '';

      if (!code) {
        if (status) {
          status.textContent = 'Ajoutez un code promo.';
          status.dataset.state = 'error';
          status.hidden = false;
        }
        input?.focus();
        return;
      }

      this.setButtonBusy(submitter, true);
      this.setBusy(true);
      try {
        const cart = await this.request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discount: code,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });

        this.updateHeaderCount(cart.item_count);
        const sectionHtml = cart.sections?.['cart-drawer'];
        if (sectionHtml) await this.renderSection(sectionHtml, true, this.returnFocus);
        else await this.refresh(true, this.returnFocus);

        const normalizedCode = code.toLowerCase();
        const applications = [
          ...(cart.cart_level_discount_applications || []),
          ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application).filter(Boolean))
        ];
        const appliedByCode = (cart.discount_codes || []).some(discount =>
          String(discount?.code || '').toLowerCase() === normalizedCode && discount?.applicable !== false
        );
        const applied = appliedByCode || applications.some(application =>
          String(application?.title || '').toLowerCase() === normalizedCode
        );

        const nextStatus = this.querySelector('[data-cart-discount-status]');
        const nextInput = this.querySelector('[data-cart-discount-input]');
        const nextDetails = nextStatus?.closest('.cart-discount');
        if (nextStatus) {
          nextStatus.textContent = applied ? 'Code promo appliqué.' : 'Ce code ne s’applique pas à ce panier.';
          nextStatus.dataset.state = applied ? 'success' : 'error';
          nextStatus.hidden = false;
        }
        if (nextInput && !applied) nextInput.value = code;
        if (nextDetails && !applied) nextDetails.open = true;

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        if (status) {
          status.textContent = error.message || 'Impossible d’appliquer ce code.';
          status.dataset.state = 'error';
          status.hidden = false;
        }
      } finally {
        this.setButtonBusy(submitter, false);
        this.setBusy(false);
      }
    }

    async addForm(form, submitter) {
      if (form.dataset.cartSubmitting === 'true') return;
      form.dataset.cartSubmitting = 'true';
      this.setButtonBusy(submitter, true);
      this.setBusy(true);
      const previousError = form.querySelector('[role="alert"]');
      if (previousError) previousError.hidden = true;
      try {
        const selectedAddons = form.classList.contains('product-form')
          ? Array.from(form.closest('product-information')?.querySelectorAll('[data-product-addon-option]:checked:not(:disabled)') || [])
          : [];
        const requestOptions = selectedAddons.length
          ? {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify(this.buildProductAddPayload(form, selectedAddons))
            }
          : this.buildStandardAddRequest(form);
        const addResponse = await this.request('cart/add.js', requestOptions);
        const formIsInDrawer = this.contains(form);
        const keepOpen = formIsInDrawer || form.classList.contains('product-form') || form.hasAttribute('data-cart-open') || this.dataset.autoOpen === 'true';
        const launcher = formIsInDrawer ? this.returnFocus : submitter;
        const sectionHtml = addResponse.sections?.['cart-drawer'];
        if (sectionHtml) await this.renderSection(sectionHtml, keepOpen, launcher);
        else if (keepOpen) this.open(launcher);

        this.setBusy(true, false);
        try { await this.syncThresholdGifts(); }
        catch {}

        const cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        await this.refresh(keepOpen, launcher);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        this.showFormError(form, error.message);
      } finally {
        delete form.dataset.cartSubmitting;
        this.setButtonBusy(submitter, false);
        this.setBusy(false);
      }
    }

    buildProductAddPayload(form, selectedAddons) {
      const source = new FormData(form);
      const parentId = Number(source.get('id'));
      if (!parentId) throw new Error(this.dataset.errorMessage || 'Cart update failed.');

      const parentItem = {
        id: parentId,
        quantity: Math.max(1, Number(source.get('quantity')) || 1)
      };
      const addonGroup = `${parentId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      for (const [name, value] of source.entries()) {
        if (name === 'id' || name === 'quantity' || name === 'sections' || name === 'sections_url') continue;
        if (name === 'selling_plan') {
          if (value) parentItem.selling_plan = Number(value);
          continue;
        }
        const propertyMatch = name.match(/^properties\[(.+)\]$/);
        if (propertyMatch && !(value instanceof File)) {
          parentItem.properties ||= {};
          parentItem.properties[propertyMatch[1]] = value;
        }
      }
      parentItem.properties ||= {};
      parentItem.properties._product_addon_group = addonGroup;
      parentItem.properties._gastrolux_ensemble = 'configuration';
      const removableHandleSelected = selectedAddons.some(addon => addon.matches('[data-handle-removable-addon]'));
      const editUrl = new URL(window.location.href);
      editUrl.searchParams.set('variant', String(parentId));
      if (removableHandleSelected) editUrl.searchParams.set('handle', 'amovible');
      else editUrl.searchParams.delete('handle');
      const extraHandleSelected = selectedAddons.some(addon => addon.matches('[data-second-handle-addon-option]'));
      if (extraHandleSelected) editUrl.searchParams.set('extra_handle', '1');
      else editUrl.searchParams.delete('extra_handle');
      const addonIds = selectedAddons
        .filter(addon => !addon.matches('[data-second-handle-addon-option]'))
        .map(addon => String(addon.value))
        .filter(Boolean);
      if (addonIds.length) editUrl.searchParams.set('addons', addonIds.join(','));
      else editUrl.searchParams.delete('addons');
      parentItem.properties._edit_url = `${editUrl.pathname}${editUrl.search}`;

      const addonItems = selectedAddons.map(addon => ({
        id: Number(addon.value),
        quantity: 1,
        parent_id: parentId,
        properties: {
          _product_addon: 'true',
          _product_addon_group: addonGroup,
          _product_addon_parent_title: addon.dataset.parentProductTitle || ''
        }
      }));

      return {
        items: [parentItem, ...addonItems],
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      };
    }

    buildStandardAddRequest(form) {
      const data = new FormData(form);
      if (!data.get('quantity')) data.set('quantity', '1');
      data.set('sections', 'cart-drawer');
      data.set('sections_url', window.location.pathname);
      return {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      };
    }

    async removeLine(button) {
      const line = button?.closest?.('[data-cart-line-key]');
      const staleKey = button?.dataset?.lineKey || line?.dataset?.cartLineKey;
      const group = line?.dataset?.productAddonGroup || '';
      const variantId = String(line?.dataset?.cartVariantId || button?.dataset?.variantId || '');
      if (!staleKey && !group && !variantId) return;

      this.setBusy(true);
      try {
        const currentCart = await this.getCart();
        let targetItem = null;

        if (group) {
          targetItem = currentCart.items?.find(item =>
            String(item.properties?._product_addon_group || '') === String(group) &&
            String(item.properties?._product_addon || '') !== 'true'
          );
        }

        if (!targetItem && staleKey) {
          targetItem = currentCart.items?.find(item => String(item.key) === String(staleKey));
        }

        if (!targetItem && variantId) {
          targetItem = currentCart.items?.find(item => String(item.variant_id) === variantId);
        }

        if (!targetItem?.key) throw new Error('Impossible de retrouver cet article dans le panier.');

        let cart = await this.request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: targetItem.key,
            quantity: 0,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });

        const giftsChanged = await this.syncThresholdGifts();
        if (giftsChanged) cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        if (giftsChanged) await this.refresh(true, this.returnFocus);
        else await this.renderSection(cart.sections?.['cart-drawer'], true, this.returnFocus);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        const target = line?.querySelector('[data-cart-line-error]');
        if (target) {
          target.textContent = error.message;
          target.hidden = false;
        }
      } finally {
        this.setBusy(false);
      }
    }

    async changeLine(key, quantity, trigger) {
      if (!key) return;
      this.setBusy(true);
      try {
        let cart = await this.request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        const giftsChanged = await this.syncThresholdGifts();
        if (giftsChanged) cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        if (giftsChanged) await this.refresh(true, this.returnFocus);
        else await this.renderSection(cart.sections?.['cart-drawer'], true, this.returnFocus);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        const line = trigger?.closest('[data-cart-line-key]');
        const target = line?.querySelector('[data-cart-line-error]');
        if (target) {
          target.textContent = error.message;
          target.hidden = false;
        }
      } finally {
        this.setBusy(false);
      }
    }

    getDismissedGiftTiers(cart) {
      return new Set(String(cart?.attributes?.gift_declined_tiers__ || '').split(',').map(tier => tier.trim()).filter(Boolean));
    }

    giftDismissalAttributes(tiers) {
      return { gift_declined_tiers__: [...tiers].sort((a, b) => Number(a) - Number(b)).join(',') };
    }

    async dismissThresholdGift(button) {
      const key = button.dataset.lineKey;
      const tier = button.dataset.giftTier;
      if (!key || !tier) return;
      this.setButtonBusy(button, true);
      this.setBusy(true);
      try {
        const currentCart = await this.getCart();
        const dismissedTiers = this.getDismissedGiftTiers(currentCart);
        dismissedTiers.add(tier);
        const updatedCart = await this.request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: { [key]: 0 },
            attributes: this.giftDismissalAttributes(dismissedTiers),
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        this.updateHeaderCount(updatedCart.item_count);
        const sectionHtml = updatedCart.sections?.['cart-drawer'];
        if (sectionHtml) await this.renderSection(sectionHtml, true, this.returnFocus);
        else await this.refresh(true, this.returnFocus);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      } catch (error) {
        this.showFormError(button.closest('[data-cart-line-key]'), error.message);
      } finally {
        this.setButtonBusy(button, false);
        this.setBusy(false);
      }
    }

    async restoreThresholdGift(button) {
      const tier = button.dataset.giftTier;
      const variantId = Number(button.dataset.variantId);
      const quantity = Math.max(1, Number(button.dataset.giftQuantity) || 1);
      if (!tier || !variantId) return;
      this.setButtonBusy(button, true);
      this.setBusy(true);
      try {
        const currentCart = await this.getCart();
        const dismissedTiers = this.getDismissedGiftTiers(currentCart);
        dismissedTiers.delete(tier);
        await this.request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: this.giftDismissalAttributes(dismissedTiers) })
        });
        const updatedCart = await this.request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity, properties: { _cart_gift_tier: tier } }],
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        const cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        const sectionHtml = updatedCart.sections?.['cart-drawer'];
        if (sectionHtml) await this.renderSection(sectionHtml, true, this.returnFocus);
        else await this.refresh(true, this.returnFocus);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        this.showFormError(button.closest('.cart-dismissed-gift'), error.message);
      } finally {
        this.setButtonBusy(button, false);
        this.setBusy(false);
      }
    }

    async addGiftWrap(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;
      this.setButtonBusy(button, true);
      this.setBusy(true);
      try {
        await this.request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _cart_gift_wrap: 'true' } }] })
        });
        const cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        await this.refresh(true, this.returnFocus);
      } catch (error) {
        this.showFormError(button.closest('.cart-gift-wrap'), error.message);
      } finally {
        this.setButtonBusy(button, false);
        this.setBusy(false);
      }
    }

    async addRecentlyViewed(button) {
      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;
      const quantity = Math.max(1, Number(button.dataset.quantity) || 1);
      this.setButtonBusy(button, true);
      this.setBusy(true);
      try {
        const addResponse = await this.request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        const sectionHtml = addResponse.sections?.['cart-drawer'];
        this.selectDrawerView('cart');
        if (sectionHtml) await this.renderSection(sectionHtml, true, this.returnFocus);

        this.setBusy(true, false);
        let giftsChanged = false;
        try { giftsChanged = await this.syncThresholdGifts(); }
        catch { giftsChanged = true; }

        const cart = await this.getCart();
        this.updateHeaderCount(cart.item_count);
        if (giftsChanged || !sectionHtml) await this.refresh(true, this.returnFocus);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        this.showFormError(this.querySelector('recently-viewed-products'), error.message);
      } finally {
        this.setButtonBusy(button, false);
        this.setBusy(false);
      }
    }

    showFormError(container, message) {
      if (!container) return;
      let target = container.querySelector('[role="alert"]');
      if (!target) {
        target = document.createElement('p');
        target.setAttribute('role', 'alert');
        if (container.matches?.('.product-form')) target.className = 'product-form__error';
        container.append(target);
      }
      target.textContent = message;
      target.hidden = false;
    }

    async syncThresholdGifts() {
      const configNode = this.querySelector('[data-cart-gift-rules]');
      if (!configNode) return false;
      let rules = [];
      try { rules = JSON.parse(configNode.textContent).rules || []; }
      catch { return false; }

      const previousSync = window.__themeCartGiftSyncPromise || Promise.resolve();
      const currentSync = previousSync
        .catch(() => false)
        .then(() => this.reconcileThresholdGifts(rules));
      window.__themeCartGiftSyncPromise = currentSync;
      try {
        return await currentSync;
      } finally {
        if (window.__themeCartGiftSyncPromise === currentSync) window.__themeCartGiftSyncPromise = null;
      }
    }

    async reconcileThresholdGifts(rules) {
      let changed = false;
      const cart = await this.getCart();
      const giftItems = cart.items.filter(item => item.properties?._cart_gift_tier);
      const qualifyingTotal = cart.items.reduce((total, item) => {
        if (item.properties?._cart_gift_tier || item.properties?._cart_gift_wrap === 'true') return total;
        return total + item.final_line_price;
      }, 0);
      const rulesByTier = new Map(
        rules
          .filter(rule => rule.tier && Number(rule.variantId) > 0 && Number(rule.threshold) > 0)
          .map(rule => [String(rule.tier), rule])
      );
      const dismissedTiers = this.getDismissedGiftTiers(cart);
      const eligibleDismissedTiers = new Set(
        [...dismissedTiers].filter(tier => {
          const rule = rulesByTier.get(tier);
          return rule && qualifyingTotal >= Number(rule.threshold);
        })
      );
      if (eligibleDismissedTiers.size !== dismissedTiers.size) {
        await this.request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: this.giftDismissalAttributes(eligibleDismissedTiers) })
        });
        changed = true;
      }
      const retainedTiers = new Set();

      for (const gift of giftItems) {
        const tier = String(gift.properties._cart_gift_tier);
        const rule = rulesByTier.get(tier);
        const eligible = Boolean(rule && qualifyingTotal >= Number(rule.threshold) && !eligibleDismissedTiers.has(tier));
        const correctVariant = Boolean(rule && Number(gift.variant_id) === Number(rule.variantId));
        const correctQuantity = Math.max(1, Number(rule?.quantity) || 1);
        const duplicate = retainedTiers.has(tier);

        if (!eligible || !correctVariant || duplicate) {
          await this.request('cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: gift.key, quantity: 0 })
          });
          changed = true;
          continue;
        }

        retainedTiers.add(tier);
        if (gift.quantity !== correctQuantity) {
          await this.request('cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: gift.key, quantity: correctQuantity })
          });
          changed = true;
        }
      }

      for (const [tier, rule] of rulesByTier) {
        if (qualifyingTotal < Number(rule.threshold) || retainedTiers.has(tier) || eligibleDismissedTiers.has(tier)) continue;
        await this.request('cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(rule.variantId), quantity: Math.max(1, Number(rule.quantity) || 1), properties: { _cart_gift_tier: tier } }] })
        });
        changed = true;
      }

      return changed;
    }

    async refresh(keepOpen = false, launcher = null) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', 'cart-drawer');
      url.searchParams.set('_cart_drawer', Date.now().toString());
      const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart refresh failed.');
      const sections = await response.json();
      const html = sections['cart-drawer'];
      if (!html) return;
      await this.renderSection(html, keepOpen, launcher);
    }

    async renderSection(html, keepOpen = false, launcher = null) {
      if (!html) return;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextWrapper = documentFragment.querySelector('#shopify-section-cart-drawer');
      const nextDrawer = nextWrapper?.querySelector('cart-drawer') || documentFragment.querySelector('cart-drawer');
      if (!nextDrawer) return;

      const currentShell = this.querySelector('.cart-drawer__shell');
      const nextShell = nextDrawer.querySelector('.cart-drawer__shell');
      if (!currentShell || !nextShell) return;

      const drawerIsOpen = Boolean(this.dialog?.open);
      const drawerIsBusy = this.panel?.getAttribute('aria-busy') === 'true';
      const persistentLoader = currentShell.querySelector('.cart-loading-overlay');
      const state = drawerIsOpen ? this.captureRenderState() : null;
      this.syncDrawerMetadata(nextDrawer);
      currentShell.className = nextShell.className;
      currentShell.replaceChildren(...Array.from(nextShell.children));
      this.panel = this.querySelector('.cart-drawer__panel');
      this.observeScrollArea();
      if (persistentLoader && this.panel) {
        const replacementLoader = this.panel.querySelector('.cart-loading-overlay');
        if (replacementLoader) replacementLoader.replaceWith(persistentLoader);
        else this.panel.prepend(persistentLoader);
      }
      if (drawerIsBusy) this.panel?.setAttribute('aria-busy', 'true');
      this.quantityTimers.clear();
      this.selectDrawerView('cart');

      if (state) this.animateRenderedState(state, currentShell, launcher);
      else this.stopReassurance();

      if (keepOpen) {
        if (!drawerIsOpen) requestAnimationFrame(() => this.open(launcher || this.returnFocus));
      }
      else if (!drawerIsOpen) document.body.classList.remove('cart-drawer-open');
    }

    syncDrawerMetadata(nextDrawer) {
      ['data-auto-open', 'data-error-message', 'data-discount-error'].forEach(name => {
        if (nextDrawer.hasAttribute(name)) this.setAttribute(name, nextDrawer.getAttribute(name));
        else this.removeAttribute(name);
      });
      const currentRules = this.querySelector('[data-cart-gift-rules]');
      const nextRules = nextDrawer.querySelector('[data-cart-gift-rules]');
      if (currentRules && nextRules) currentRules.textContent = nextRules.textContent;
    }

    captureRenderState() {
      const shell = this.querySelector('.cart-drawer__shell');
      const scrollContainer = this.querySelector('[data-cart-drawer-scroll]');
      const activeElement = document.activeElement;
      const activeLine = activeElement?.closest?.('[data-cart-line-key]');
      const itemStates = new Map();

      this.querySelectorAll('[data-cart-line-key]').forEach((element, index) => {
        itemStates.set(element.dataset.cartLineKey, {
          clone: element.cloneNode(true),
          index,
          rect: element.getBoundingClientRect(),
          signature: element.textContent.replace(/\s+/g, ' ').trim()
        });
      });

      return {
        activeControl: activeElement?.matches?.('.quantity-selector__input') ? 'quantity' : activeElement?.matches?.('[data-cart-remove]') ? 'remove' : null,
        activeLineKey: activeLine?.dataset.cartLineKey || null,
        giftOpen: Boolean(this.querySelector('.cart-gift-wrap__details')?.open),
        itemStates,
        progress: Number(this.querySelector('.cart-rewards__bar')?.dataset.cartProgress || 0),
        scrollTop: scrollContainer?.scrollTop || 0,
        shellRect: shell?.getBoundingClientRect()
      };
    }

    animateRenderedState(state, shell, launcher) {
      const scrollContainer = this.querySelector('[data-cart-drawer-scroll]');
      if (scrollContainer) {
        const maxScroll = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
        scrollContainer.scrollTop = Math.min(state.scrollTop, maxScroll);
      }

      const giftDetails = this.querySelector('.cart-gift-wrap__details');
      if (giftDetails && state.giftOpen) giftDetails.open = true;

      const currentItems = new Map();
      this.querySelectorAll('[data-cart-line-key]').forEach(element => {
        currentItems.set(element.dataset.cartLineKey, element);
      });

      const addedItems = [];
      currentItems.forEach((element, key) => {
        const previous = state.itemStates.get(key);
        if (!previous) {
          addedItems.push(element);
          if (!this.reducedMotion) {
            element.animate([
              { opacity: 0, transform: 'translate3d(0, 12px, 0)' },
              { opacity: 1, transform: 'translate3d(0, 0, 0)' }
            ], { duration: 320, easing: 'cubic-bezier(.22, 1, .36, 1)' });
          }
          return;
        }

        if (this.reducedMotion) return;
        const currentRect = element.getBoundingClientRect();
        const deltaX = previous.rect.left - currentRect.left;
        const deltaY = previous.rect.top - currentRect.top;
        const contentChanged = previous.signature !== element.textContent.replace(/\s+/g, ' ').trim();
        if (Math.abs(deltaX) < .5 && Math.abs(deltaY) < .5 && !contentChanged) return;
        element.animate([
          { opacity: contentChanged ? .68 : 1, transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ], { duration: 280, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      });

      state.itemStates.forEach((previous, key) => {
        if (currentItems.has(key) || !state.shellRect) return;
        const clone = previous.clone;
        clone.removeAttribute('data-cart-line-key');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        Object.assign(clone.style, {
          background: 'var(--color-background)',
          height: `${previous.rect.height}px`,
          left: `${previous.rect.left - state.shellRect.left}px`,
          margin: '0',
          pointerEvents: 'none',
          position: 'absolute',
          top: `${previous.rect.top - state.shellRect.top}px`,
          width: `${previous.rect.width}px`,
          zIndex: '4'
        });
        shell.append(clone);
        if (this.reducedMotion) {
          clone.remove();
          return;
        }
        const animation = clone.animate([
          { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
          { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(.985)' }
        ], { duration: 180, easing: 'cubic-bezier(.4, 0, 1, 1)' });
        animation.finished.then(() => clone.remove()).catch(() => clone.remove());
      });

      const incomingEmptyState = this.querySelector('.cart-drawer__empty');
      if (incomingEmptyState && state.itemStates.size && !this.reducedMotion) {
        incomingEmptyState.animate([
          { opacity: 0, transform: 'translate3d(0, 8px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' }
        ], { duration: 320, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      }

      this.restoreDrawerFocus(state, launcher);
      this.setBusy(false);
      this.startReassurance();

      if (addedItems.length) {
        const revealTarget = addedItems.find(element => element.classList.contains('cart-drawer-item--gift')) || addedItems[addedItems.length - 1];
        requestAnimationFrame(() => revealTarget.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest' }));
      }
    }

    restoreDrawerFocus(state, launcher) {
      let target = null;
      if (state.activeLineKey) {
        const line = Array.from(this.querySelectorAll('[data-cart-line-key]')).find(element => element.dataset.cartLineKey === state.activeLineKey);
        if (state.activeControl === 'quantity') target = line?.querySelector('.quantity-selector__input');
        else if (state.activeControl === 'remove') target = line?.querySelector('[data-cart-remove]');
      }
      if (!target && launcher instanceof HTMLElement && this.contains(launcher)) target = launcher;
      if (!target && state.activeLineKey) target = this.querySelector('[data-cart-drawer-close]');
      target?.focus?.({ preventScroll: true });
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(element => { element.textContent = count; });
      document.querySelectorAll('[data-cart-text-template]').forEach(element => {
        element.textContent = element.dataset.cartTextTemplate.replace('__COUNT__', count);
      });
      document.querySelectorAll('[data-cart-count-link]').forEach(element => {
        if (element.dataset.cartLabelTemplate) element.setAttribute('aria-label', element.dataset.cartLabelTemplate.replace('__COUNT__', count));
      });
    }

    goToReassurance(index) {
      const reassurance = this.querySelector('[data-cart-reassurance]');
      const track = reassurance?.querySelector('[data-cart-reassurance-track]');
      const slides = track?.children;
      const dots = reassurance?.querySelectorAll('[data-slider-dot]');
      if (!track || !slides?.length) return;
      this.reassuranceIndex = (index + slides.length) % slides.length;
      const slideHeight = slides[0].getBoundingClientRect().height;
      track.style.transform = `translateY(-${this.reassuranceIndex * slideHeight}px)`;
      dots?.forEach((dot, dotIndex) => dot.setAttribute('aria-current', dotIndex === this.reassuranceIndex ? 'true' : 'false'));
    }

    startReassurance(reset = true) {
      this.stopReassurance();
      const reassurance = this.querySelector('[data-cart-reassurance]');
      const track = reassurance?.querySelector('[data-cart-reassurance-track]');
      const slides = track?.children;
      if (!track || !slides || slides.length < 2) return;
      if (reset) this.reassuranceIndex = 0;
      this.goToReassurance(this.reassuranceIndex);
      if (this.reducedMotion) return;
      this.reassuranceTimer = window.setInterval(() => {
        this.goToReassurance(this.reassuranceIndex + 1);
      }, 3600);
    }

    stopReassurance() {
      window.clearInterval(this.reassuranceTimer);
      this.reassuranceTimer = null;
    }

    disconnectedCallback() {
      this.scrollObserver?.disconnect();
      this.abortController?.abort();
      this.quantityTimers?.forEach(timer => window.clearTimeout(timer));
      this.stopReassurance();
    }
  });
}
